import React, { createContext, useState, useContext } from 'react';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  sources?: string[];
}

const WELCOME_MESSAGE: Message = {
  id: '1',
  text: "I'm Ask Athlyte Bot, your personal sports analytics assistant! I can help you explore stats, frame sports queries for the Athlyte search feature, or answer questions about this platform. How can I assist you today?",
  sender: 'bot',
  timestamp: new Date(),
};

interface ChatHistoryContextType {
  messages: Message[];
  getCurrentMessages: () => Message[];
  addMessage: (message: Message) => void;
  addMessages: (messages: Message[]) => void;
  initializeChat: () => void;
  clearHistory: () => void;
  resetChat: () => void;
}

const ChatHistoryContext = createContext<ChatHistoryContextType | undefined>(undefined);

/**
 * Provider component for managing chat histories globally
 * This ensures chat state persists across component remounts
 * and is shared across the entire app
 */
export const ChatHistoryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);

  const getCurrentMessages = () => {
    return messages;
  };

  const addMessage = (message: Message) => {
    setMessages(prev => [...prev, message]);
  };

  const addMessages = (newMessages: Message[]) => {
    setMessages(prev => [...prev, ...newMessages]);
  };

  const initializeChat = () => {
    if (!isInitialized && messages.length === 0) {
      setMessages([{ ...WELCOME_MESSAGE, timestamp: new Date() }]);
      setIsInitialized(true);
    }
  };

  const clearHistory = () => {
    setMessages([]);
    setIsInitialized(false);
  };

  const resetChat = () => {
    setMessages([{ ...WELCOME_MESSAGE, timestamp: new Date() }]);
    setIsInitialized(true);
  };

  return (
    <ChatHistoryContext.Provider value={{
      messages,
      getCurrentMessages,
      addMessage,
      addMessages,
      initializeChat,
      clearHistory,
      resetChat,
    }}>
      {children}
    </ChatHistoryContext.Provider>
  );
};

/**
 * Custom hook to use chat history context
 */
export const useChatHistory = () => {
  const context = useContext(ChatHistoryContext);
  if (!context) {
    throw new Error('useChatHistory must be used within ChatHistoryProvider');
  }
  return context;
};
